// Location privacy helpers — shared by the API (to coarsen what it returns) and the
// web app (to draw the "approximate area" circle at the same radius).
//
// When a host hides an event's exact location until they approve an RSVP, the API
// must never send the precise coordinates or address to an un-approved viewer — not
// even in the JSON, where a curious user could read them from the network tab. So we
// round the coordinates to a coarse grid and the UI shows a soft circle over that
// area instead of a pin. The grid step (~1.1 km at 2 decimal places) is comfortably
// smaller than the displayed radius, so the true point always sits inside the circle.

/** Radius (km) of the "approximate area" circle drawn for a hidden location. */
export const APPROX_AREA_RADIUS_KM = 1.6

/** Round one coordinate to ~1.1 km grid (2 decimal places). */
export function coarsenCoord(value: number): number {
  return Math.round(value * 100) / 100
}

/** Coarsen a lat/lng pair for public display of a hidden location. */
export function coarsenLocation(lat: number, lng: number): { lat: number; lng: number } {
  return { lat: coarsenCoord(lat), lng: coarsenCoord(lng) }
}

/**
 * Derive a coarse, shareable area label ("Chattanooga, TN") from a full address,
 * dropping the street line and any ZIP/postal code. Returns null when nothing
 * usable remains. Safe to show for a hidden location — it reveals the general
 * area, never the street.
 */
export function extractAreaLabel(addressText: string | null | undefined): string | null {
  if (!addressText) return null
  const parts = addressText.split(',').map((p) => p.trim()).filter(Boolean)
  if (parts.length < 2) return null
  // Drop the first segment (street line); keep city / state / region.
  const rest = parts.slice(1).map((seg) =>
    seg
      .replace(/\b\d{5}(-\d{4})?\b/g, '') // strip US ZIP / ZIP+4
      .replace(/\s{2,}/g, ' ')
      .trim(),
  )
  const cleaned = rest.filter((seg) => seg && !/^(usa|united states)$/i.test(seg))
  return cleaned.length ? cleaned.join(', ') : null
}
